/**
 * ProductLinkSummaryModal
 *
 * Triggered by the 🔗 link icon on a product row in the dashboard.
 * Shows a summary of product info + linked products.
 * "Edit in Hierarchy" navigates to /product-hierarchy with pre-selection:
 *   - COMMERCIAL → pre-select that commercial product
 *   - TECHNICAL  → pre-select its first linked commercial product
 *   - PACKAGE    → pre-select the package + its first linked commercial product
 */

import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import api from '../../services/api';
import hierarchyService from '../../services/hierarchyService';
import ProductTypeIcon from '../ProductTypeIcon';
import './ProductLinkSummaryModal.css';

const PRODUCT_TYPE_LABELS = {
  TECHNICAL: 'Technical Product',
  COMMERCIAL: 'Commercial Product',
  PACKAGE: 'Package',
};

const ProductLinkSummaryModal = ({ productId, onClose }) => {
  const history = useHistory();
  const [product, setProduct] = useState(null);
  const [linkedProducts, setLinkedProducts] = useState([]);
  const [linkedPackages, setLinkedPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await api.getProductById(productId);
        const prod = res.data.data.product || res.data.data;
        setProduct(prod);

        if (prod.product_type === 'COMMERCIAL') {
          const r = await hierarchyService.getTechnicalProductsForCommercial(prod.id);
          setLinkedProducts((r.data.data.technicalProducts || []).filter(p => p.linked));
          const pkgRes = await hierarchyService.getPackagesForCommercial(prod.id);
          setLinkedPackages(pkgRes.data.data.packages || []);
        } else if (prod.product_type === 'PACKAGE') {
          const r = await hierarchyService.getCommercialProductsForPackage(prod.id);
          setLinkedProducts(r.data.data.linked || []);
        } else if (prod.product_type === 'TECHNICAL') {
          const r = await hierarchyService.getCommercialProductsForTechnical(prod.id);
          setLinkedProducts(r.data.data.commercialProducts || []);
        }
      } catch (err) {
        setError('Failed to load product details.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [productId]);

  const handleEditInHierarchy = () => {
    let state = {};

    if (product.product_type === 'COMMERCIAL') {
      state = { preSelectCommercialId: product.id };
    } else if (product.product_type === 'TECHNICAL') {
      state = { preSelectCommercialId: linkedProducts[0]?.id };
    } else if (product.product_type === 'PACKAGE') {
      state = {
        preSelectPackageId: product.id,
        preSelectCommercialId: linkedProducts[0]?.id,
      };
    }

    history.push('/product-hierarchy', state);
    onClose();
  };

  const linkedLabel = product?.product_type === 'COMMERCIAL'
    ? 'Linked Technical Products'
    : product?.product_type === 'PACKAGE'
      ? 'Linked Commercial Products'
      : 'Linked Commercial Products';

  return (
    <div className="pls-modal__backdrop" onClick={onClose}>
      <div className="pls-modal__panel" onClick={e => e.stopPropagation()}>

        <div className="pls-modal__header">
          <div className="pls-modal__title-row">
            {product && <ProductTypeIcon productType={product.product_type} size={18} />}
            <h2 className="pls-modal__title">
              {product ? product.product_name : 'Product Summary'}
            </h2>
            {product && (
              <span className="pls-modal__status-badge" data-status={product.status}>
                {product.status}
              </span>
            )}
          </div>
          <button className="pls-modal__close-btn" onClick={onClose} title="Close">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>

        {loading && (
          <div className="pls-modal__loading">
            <div className="pls-modal__spinner" />
            <span>Loading...</span>
          </div>
        )}

        {error && <p className="pls-modal__error">{error}</p>}

        {!loading && !error && product && (
          <>
            <div className="pls-modal__body">
              <div className="pls-modal__grid">
                <div className="pls-modal__field">
                  <span className="pls-modal__label">Product Name</span>
                  <span className="pls-modal__value">{product.product_name || '—'}</span>
                </div>
                <div className="pls-modal__field">
                  <span className="pls-modal__label">Product Type</span>
                  <span className="pls-modal__value">
                    <ProductTypeIcon productType={product.product_type} size={13} />
                    {PRODUCT_TYPE_LABELS[product.product_type] || product.product_type || '—'}
                  </span>
                </div>
                <div className="pls-modal__field">
                  <span className="pls-modal__label">Release Type</span>
                  <span className="pls-modal__value">{product.release_type || '—'}</span>
                </div>
                <div className="pls-modal__field">
                  <span className="pls-modal__label">Owner</span>
                  <span className="pls-modal__value">
                    {product.owner
                      ? `${product.owner.first_name || ''} ${product.owner.last_name || ''}`.trim() || '—'
                      : '—'}
                  </span>
                </div>
                {product.description && (
                  <div className="pls-modal__field pls-modal__field--full">
                    <span className="pls-modal__label">Description</span>
                    <span className="pls-modal__value pls-modal__value--multiline">
                      {product.description}
                    </span>
                  </div>
                )}
              </div>

              {product.product_type === 'COMMERCIAL' && (
                <div className="pls-modal__linked-section">
                  <h3 className="pls-modal__linked-title">Linked Packages</h3>
                  {linkedPackages.length === 0 ? (
                    <p className="pls-modal__linked-empty">Not linked to any packages.</p>
                  ) : (
                    <ul className="pls-modal__linked-list">
                      {linkedPackages.map(p => (
                        <li key={p.id} className="pls-modal__linked-item">
                          <ProductTypeIcon productType={p.product_type} size={13} />
                          <span className="pls-modal__linked-name">{p.product_name}</span>
                          <span className={`pls-modal__linked-status pls-modal__linked-status--${p.status}`}>
                            {p.status}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              <div className="pls-modal__linked-section">
                <h3 className="pls-modal__linked-title">{linkedLabel}</h3>
                {linkedProducts.length === 0 ? (
                  <p className="pls-modal__linked-empty">No linked products.</p>
                ) : (
                  <ul className="pls-modal__linked-list">
                    {linkedProducts.map(p => (
                      <li key={p.id} className="pls-modal__linked-item">
                        <ProductTypeIcon productType={p.product_type} size={13} />
                        <span className="pls-modal__linked-name">{p.product_name}</span>
                        <span className={`pls-modal__linked-status pls-modal__linked-status--${p.status}`}>
                          {p.status}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="pls-modal__footer">
              <button className="pls-modal__btn pls-modal__btn--secondary" onClick={onClose}>
                Close
              </button>
              <button className="pls-modal__btn pls-modal__btn--primary" onClick={handleEditInHierarchy}>
                <svg width="14" height="14" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
                Edit in Hierarchy
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProductLinkSummaryModal;
